"use client";

import hotelRoomSearchSchema from "@/schemas/hotelRoomSearchSchema";
import { staticDataService } from "@/services/staticService";
import { formatDate } from "@/utils/dateFormatter";
import { getLocalizedPath } from "@/utils/pathUtils";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useLocale } from "next-intl";
import { useCallback, useState } from "react";

export const useHotelRoomSearch = (initialData = {}, locationSlug) => {
  // Tarih geçerliliğini kontrol et
  const isValidDate = (date) => {
    if (!date) return false;
    const dateObj = new Date(date);
    // 1970'ten önceki tarihleri veya geçersiz tarihleri reddet
    return !isNaN(dateObj.getTime()) && dateObj.getTime() > new Date('1970-01-02').getTime();
  };
  
  // Default tarihleri hesapla: bugün ve bugünden 4 gün sonra
  const getDefaultDates = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkout = new Date(today);
    checkout.setDate(checkout.getDate() + 4);
    return { today, checkout };
  };
  
  const [formState, setFormState] = useState(() => {
    const defaultDates = getDefaultDates();
    // Eğer initialData'da tarih yoksa veya geçersizse (1970 gibi), default tarihleri kullan
    return {
      checkinDate: isValidDate(initialData.checkinDate) 
        ? initialData.checkinDate 
        : defaultDates.today,
      checkoutDate: isValidDate(initialData.checkoutDate) 
        ? initialData.checkoutDate 
        : defaultDates.checkout,
      guests: initialData.guests || { adult: 1, child: 0, childrenAges: [] },
      publicHolidays: [],
    };
  });

  const locale = useLocale();
  const t = useTranslationDetector("hotelSearch");

  const setCheckinDate = (checkinDate) => {
    setFormState((prevState) => ({ ...prevState, checkinDate }));
  };

  const setCheckoutDate = (checkoutDate) => {
    setFormState((prevState) => ({ ...prevState, checkoutDate }));
  };

  const setGuests = (guests) => {
    setFormState((prevState) => ({ ...prevState, guests }));
  };

  const setPublicHolidays = (publicHolidays) => {
    setFormState((prevState) => ({ ...prevState, publicHolidays }));
  };

  // Utility functions
  const formatDateForUrl = useCallback((date) => {
    return formatDate(date, { format: "YYYY-MM-DD" });
  }, []);

  // Hotel için URL oluşturma
  // Format: /otel/{hotelSlug}?check_in=2025-12-11&check_out=2025-12-14&adult_1=1&child_1=2&childage_1_1=2&childage_1_2=8
  const generateHotelRoomSearchUrl = useCallback(() => {
    const checkinDateStr = formatDateForUrl(formState.checkinDate);
    const checkoutDateStr = formatDateForUrl(formState.checkoutDate);

    // Query parametreleri oluştur
    const queryParams = new URLSearchParams();

    // Tarihleri query parametrelerine ekle
    if (checkinDateStr) {
      queryParams.set("check_in", checkinDateStr);
    }
    if (checkoutDateStr) {
      queryParams.set("check_out", checkoutDateStr);
    }

    // Misafir bilgilerini ekle (hotel formatı: adult_1, child_1, childage_1_1, childage_1_2, ...)
    if (formState.guests.adult > 0) {
      queryParams.set("adult_1", formState.guests.adult.toString());
    }
    
    if (formState.guests.child > 0) {
      queryParams.set("child_1", formState.guests.child.toString());
      
      // Çocuk yaşlarını childage_1_1, childage_1_2, ... formatında ekle
      const childrenAges = formState.guests.childrenAges || [];
      childrenAges.forEach((age, index) => {
        queryParams.set(`childage_1_${index + 1}`, age.toString());
      });
    }

    const queryString = queryParams.toString();
    // URL formatı: /otel/{hotelSlug}?check_in=...&check_out=...&adult_1=...&child_1=...&childage_1_1=...
    // Locale'e göre path'i belirle
    const basePath = getLocalizedPath('/hotel', locale);
    const baseUrl = `${basePath}/${locationSlug}`;

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [
    formState.checkinDate,
    formState.checkoutDate,
    formatDateForUrl,
    formState.guests,
    locationSlug,
    locale,
  ]);

  // Validation
  const validateForm = useCallback(() => {
    const formData = {
      location: locationSlug,
    };

    const errors = {};

    try {
      hotelRoomSearchSchema.parse(formData);
    } catch (error) {
      error.errors?.forEach((err) => {
        errors[err.path[0]] = err.message;
      });
    }

    // Çocuk varsa yaş seçimi zorunlu
    if (
      formState.guests.child > 0 &&
      (formState.guests.childrenAges?.length || 0) !== formState.guests.child
    ) {
      errors.childrenAges = t("childrenAgesRequiredMessage");
    }

    return errors;
  }, [locationSlug, formState.guests, t]);

  const getPublicHolidays = async () => {
    try {
      const publicHolidaysResponse = await staticDataService.getStaticData({
        LanguageCode: locale.toUpperCase(),
        PageName: "PublicHolidays",
        GroupNames: [
          new Date().getMonth() === 11
            ? new Date().getFullYear() + 1
            : new Date().getFullYear(),
        ],
      });
      const publicHolidays = JSON.parse(
        publicHolidaysResponse?.ContentParts[0]?.ContentGroupKeys[0]
          ?.ContentGroupValues[0]?.Value,
      ).map((item) => ({
        date: item.date,
        title: item.localName,
      }));
      setPublicHolidays(publicHolidays);
    } catch (error) {
      console.error(
        "getPublicHolidays: Public holidays yüklenirken hata oluştu:",
        error,
      );
    }
  };

  return {
    // Hotel-specific state mapping
    checkinDate: formState.checkinDate,
    checkoutDate: formState.checkoutDate,
    guests: formState.guests,
    publicHolidays: formState.publicHolidays,

    // Actions
    setCheckinDate,
    setCheckoutDate,
    setGuests,

    // Utility functions
    validateForm,
    generateHotelRoomSearchUrl,
    refreshPublicHolidays: getPublicHolidays,

    // State for compatibility
    state: formState,
  };
};
